"use client";
import { StartEndTimePicker } from "@cmp-src/date-time/StartEndTimePicker/StartEndTimePicker";
import { CalendarInput } from "@cmp-src/form/calendar/CalendarInput/CalendarInput";
import { SelectOffices } from "@cmp-src/form/OfficeSelectorInput/SelectOffices";
import { ArrowDownIcon } from "@cmp-src/icons/icons";
import type { Office } from "@gettactic/api";
import { getDefaultsHoursFromOffice } from "@gettactic/helpers";
import { getNowDate } from "@gettactic/helpers/src/date-time/getNowDate";
import { useAuthenticated, useDefaultOfficeHours } from "@gettactic/hooks";
import React from "react";
import { MapPageContext } from "@/features/map/MapProvider";
import { convertTZ } from "@/lib/utils";
import { useGlobalSearch } from "./GlobalSearchProvider";

export function GlobalSearchControls() {
  const mapContext = React.useContext(MapPageContext);
  const { userContext } = useAuthenticated();
  const globalSearchContext = useGlobalSearch();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [hasScrolled, setHasScrolled] = React.useState(false);

  // Use userContext.currentOffice only for timezone/initialization, not for selection
  const currentOffice = userContext.currentOffice;

  // Get selected offices from command palette context
  // Support both single and multiselect modes
  const selectedOfficeIds = globalSearchContext.selectedOfficeIds;
  const selectedOfficeId = globalSearchContext.selectedOfficeId;

  // Track previous currentOffice to detect external changes (e.g., from map)
  const prevCurrentOfficeIdRef = React.useRef(currentOffice?.id);

  // Sync selectedOfficeIds when currentOffice changes externally (e.g., from map navigation)
  React.useEffect(() => {
    const prevId = prevCurrentOfficeIdRef.current;
    const currentId = currentOffice?.id;

    // Update ref for next comparison
    prevCurrentOfficeIdRef.current = currentId;

    // Only sync if currentOffice actually changed (not on initial mount)
    if (prevId && currentId && prevId !== currentId) {
      // currentOffice changed externally, sync the dropdown selection
      globalSearchContext.setSelectedOfficeIds([currentId]);
      globalSearchContext.setSelectedOfficeId(currentId);
    }
  }, [currentOffice?.id]);

  // For multiselect: use selectedOfficeIds array
  // For single select: use selectedOfficeId
  const selectedOffices = React.useMemo(() => {
    if (selectedOfficeIds.length > 0) {
      return selectedOfficeIds
        .map((id) =>
          userContext.authenticatedUser?.offices?.offices?.find(
            (o: Office) => o.id === id
          )
        )
        .filter((o): o is Office => o !== undefined);
    }

    // Fall back to single select
    if (selectedOfficeId === undefined) {
      return currentOffice ? [currentOffice] : [];
    }
    if (selectedOfficeId === null) {
      return [];
    }
    const office = userContext.authenticatedUser?.offices?.offices?.find(
      (o: Office) => o.id === selectedOfficeId
    );
    return office ? [office] : [];
  }, [
    selectedOfficeIds,
    selectedOfficeId,
    userContext.authenticatedUser?.offices?.offices,
    currentOffice
  ]);

  const selectedOffice = selectedOffices.length > 0 ? selectedOffices[0] : null;

  // Track scroll state of the scrollable content area below
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Find the next sibling element (the scrollable content area)
    const scrollableElement = container.nextElementSibling as HTMLElement;
    if (!scrollableElement) return;

    const handleScroll = () => {
      setHasScrolled(scrollableElement.scrollTop > 0);
    };

    scrollableElement.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      scrollableElement.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const officeForTimezone = selectedOffice || currentOffice;
  const defaultOfficeHoursCalculator = useDefaultOfficeHours(
    officeForTimezone || currentOffice
  );

  const defaultOfficeHoursDates = React.useMemo(() => {
    const today = convertTZ(
      new Date(),
      officeForTimezone.time_zone ?? userContext.deviceTz
    );
    const defaultOfficeHours = defaultOfficeHoursCalculator(today);
    if (!defaultOfficeHours) {
      return null;
    }

    const startDate = new Date(today.getTime());
    startDate.setHours(
      defaultOfficeHours.startHour,
      defaultOfficeHours.startMinutes,
      0
    );
    const endDate = new Date(today.getTime());
    endDate.setHours(
      defaultOfficeHours.endHour,
      defaultOfficeHours.endMinutes,
      0
    );
    return {
      startDate,
      endDate
    };
  }, [officeForTimezone, defaultOfficeHoursCalculator]);

  // Use map context values if available, otherwise use command palette context
  const startDate = mapContext?.startDate || globalSearchContext.startDate;
  const endDate = mapContext?.endDate || globalSearchContext.endDate;
  const changeStartDate =
    mapContext?.changeStartDate || globalSearchContext.setStartDate;
  const changeEndDate =
    mapContext?.changeEndTime || globalSearchContext.setEndDate;
  const timeMode = mapContext?.timeMode || globalSearchContext.timeMode;
  const setTimeMode =
    mapContext?.setTimeMode || globalSearchContext.setTimeMode;

  // Initialize command palette dates if not set - use same logic as map
  React.useEffect(() => {
    if (!currentOffice) {
      return;
    }
    // Only initialize if dates are not set yet
    if (
      globalSearchContext.startDate !== null ||
      globalSearchContext.endDate !== null
    ) {
      return;
    }

    if (!defaultOfficeHoursDates) {
      return;
    }

    globalSearchContext.setStartDate(defaultOfficeHoursDates.startDate);
    globalSearchContext.setEndDate(defaultOfficeHoursDates.endDate);
  }, [
    currentOffice?.time_zone,
    currentOffice?.id,
    defaultOfficeHoursDates,
    globalSearchContext.startDate,
    globalSearchContext.endDate
  ]);

  React.useEffect(() => {
    // Only restore defaults when we *exit* live mode (live -> non-live).
    if (timeMode === "live" || !defaultOfficeHoursDates) {
      return;
    }

    changeStartDate(defaultOfficeHoursDates.startDate);
    changeEndDate(defaultOfficeHoursDates.endDate);
  }, [timeMode, defaultOfficeHoursDates, changeStartDate, changeEndDate]);

  const setSelectedDate = (date: Date | Date[] | undefined) => {
    const dates = Array.isArray(date) ? date : [date];
    if (dates.length === 1 && dates[0]) {
      // Preserve the time from current startDate/endDate when changing date
      const newDate = new Date(dates[0]);
      if (startDate) {
        newDate.setHours(
          startDate.getHours(),
          startDate.getMinutes(),
          startDate.getSeconds(),
          startDate.getMilliseconds()
        );
      } else {
        newDate.setHours(0, 0, 0, 0);
      }
      changeStartDate(newDate);

      // Update end date to same date, preserving end time
      const newEndDate = new Date(dates[0]);
      if (endDate) {
        newEndDate.setHours(
          endDate.getHours(),
          endDate.getMinutes(),
          endDate.getSeconds(),
          endDate.getMilliseconds()
        );
      } else {
        newEndDate.setHours(23, 59, 59, 999);
      }
      changeEndDate(newEndDate);
    } else {
      // Handle multiple dates or undefined
      const selectedDate = dates[0] || getNowDate();
      if (startDate) {
        selectedDate.setHours(
          startDate.getHours(),
          startDate.getMinutes(),
          startDate.getSeconds(),
          startDate.getMilliseconds()
        );
      } else {
        selectedDate.setHours(0, 0, 0, 0);
      }
      changeStartDate(selectedDate);

      const newEndDate = new Date(selectedDate);
      if (endDate) {
        newEndDate.setHours(
          endDate.getHours(),
          endDate.getMinutes(),
          endDate.getSeconds(),
          endDate.getMilliseconds()
        );
      } else {
        newEndDate.setHours(23, 59, 59, 999);
      }
      changeEndDate(newEndDate);
    }
  };

  const handleOfficeChange = (officeIds: string[]) => {
    globalSearchContext.setSelectedOfficeIds(officeIds);
    // Also update single select for backward compatibility
    globalSearchContext.setSelectedOfficeId(
      officeIds.length > 0 ? officeIds[0] : null
    );
  };

  return (
    <div
      ref={containerRef}
      className={`flex flex-shrink-0 items-center justify-between gap-2 border-0 px-3 py-3 transition-shadow duration-200 ${
        hasScrolled ? "shadow-md" : ""
      }`}
    >
      <div className="flex h-[42px] w-full min-w-0 flex-1 flex-row items-center justify-center gap-2 whitespace-nowrap rounded-v2 border border-neutral-200 bg-background px-3 py-2 font-medium text-xs ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-80 md:rounded-lg [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0">
        <SelectOffices
          offices={
            selectedOfficeIds.length > 0
              ? selectedOfficeIds
              : selectedOffice
                ? [selectedOffice.id]
                : []
          }
          onChange={handleOfficeChange}
          multiselect
          withApply
          splitView
          placeholder="All Offices"
          contentWidth="480px"
          trigger={
            <button className="group inline-flex w-full items-center gap-1">
              <span className="min-w-0 truncate text-left font-medium text-sm">
                {selectedOffices.length === 0
                  ? "All Offices"
                  : selectedOffices.length === 1
                    ? selectedOffices[0].name
                    : `${selectedOffices.length} selected`}
              </span>
              <ArrowDownIcon className="size-3 flex-shrink-0" />
            </button>
          }
        />
      </div>
      <div className="flex items-center gap-2">
        <div className={"relative flex items-center gap-2"}>
          <CalendarInput
            mode="single"
            variant="outlined"
            onSelect={setSelectedDate}
            selectedDate={startDate || getNowDate()}
            setValueOnApply
            withArrows={true}
          />
          <StartEndTimePicker
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={changeStartDate}
            onEndDateChange={changeEndDate}
            mode={timeMode}
            onModeChange={setTimeMode}
            allowLive
            className={"mt-0 w-[230px] min-w-[230px]"}
            timeTriggerClassName="text-[13px]"
            timeZone={currentOffice?.time_zone}
            officeStartHour={
              getDefaultsHoursFromOffice(currentOffice).startHour
            }
            officeEndHour={getDefaultsHoursFromOffice(currentOffice).endHour}
            defaultMeetingDuration={Math.round(
              getDefaultsHoursFromOffice(currentOffice).meetingRoomDuration * 60
            )}
          />
        </div>
      </div>
    </div>
  );
}
